import React, { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Formik, FormikProps } from 'formik';
import noop from 'lodash/noop';
import { MyPeopleEvalFiltersForm } from '../MyPeopleEvalFiltersForm/MyPeopleEvalFiltersForm';
import { EvalTypeToggles } from '../EvalTypeToggles/EvalTypeToggles';
import { DepartmentData } from '@shared/interfaces/DepartmentData';
import { BaseFiltersDrawer } from '@shared/components/BaseFiltersDrawer/BaseFiltersDrawer';
import { EvalFiltersFields } from '../../interfaces/EvalFiltersFields';
import { EvalFilterFields } from '../../enums/EvalFilterFields';
import { SearchParamObject } from '@shared/interfaces/SearchParamObject';
import { LocationOptionData } from '@shared/interfaces/LocationOptionData';
import { EvalStatuses } from '../../enums/EvalStatuses';

interface Props {
  departments: DepartmentData[];
  locations: LocationOptionData[];
  isLoading: boolean;
  initialValues: EvalFiltersFields;
  isFilterLoading: boolean;
}

export const MyPeopleEvalFilters: React.FC<Props> = ({
  departments,
  locations,
  isLoading,
  initialValues,
  isFilterLoading,
}) => {
  const formRef = useRef<FormikProps<EvalFiltersFields>>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsObject = (): SearchParamObject => {
    const obj: SearchParamObject = {};
    for (const [key, value] of searchParams.entries()) {
      obj[key] = value;
    }
    return obj;
  };

  const TYPE_TOGGLE_KEYS = [EvalFilterFields.IncludeRegular, EvalFilterFields.IncludeEop];
  const filtersLength = Object.keys(searchParamsObject()).filter((key) => !TYPE_TOGGLE_KEYS.includes(key as EvalFilterFields)).length;

  const handleResetFilters = () => {
    const currentParams = searchParamsObject();
    const preservedParams: SearchParamObject = {};
    for (const key of TYPE_TOGGLE_KEYS) {
      if (currentParams[key]) {
        preservedParams[key] = currentParams[key];
      }
    }
    setSearchParams(preservedParams);
    formRef.current?.setValues({
      status: [EvalStatuses.Upcoming, EvalStatuses.Ongoing, EvalStatuses.PendingToClose, EvalStatuses.PendingToStart],
      locations: [],
      managers: [],
      members: [],
      dateFrom: null,
      dateTo: null,
      departments: [],
      includeRegular: formRef.current?.values.includeRegular ?? true,
      includeEop: formRef.current?.values.includeEop ?? true,
      eopStatus: null,
      isRescheduled: false,
      eopResolvedAtFrom: null,
      eopResolvedAtTo: null,
      eopExtendedToFrom: null,
      eopExtendedToTo: null,
      employmentType: null,
      hasOngoingFlag: false,
      engagementModels: [],
    });
  };

  return (
    <Formik initialValues={initialValues} innerRef={formRef} onSubmit={noop} validateOnMount>
      {() => (
        <BaseFiltersDrawer
          isLoading={isLoading}
          filtersCount={filtersLength}
          onReset={handleResetFilters}
          renderDrawer={!isFilterLoading}
          headerExtra={<EvalTypeToggles departments={departments} locations={locations} />}
        >
          <MyPeopleEvalFiltersForm departments={departments} locations={locations} />
        </BaseFiltersDrawer>
      )}
    </Formik>
  );
};
